import { useCallback, useEffect, useRef } from 'react';

import type { VideoTimeUpdate } from '@rover/components/src/video/VideoPlayer';
import type { GalleryVideoItem } from '@rover/react-lib/src/components/ImageGallery/ImageGallery.common';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import { IntroVideoWatchProgressEvent } from './analyticsEvents';
import useIntroVideoViewTracking, { getPlatform } from './useIntroVideoViewTracking';

type Params = Parameters<typeof useIntroVideoViewTracking>[0];

type PlayedRange = [start: number, end: number];

type WatchSession = {
  playedVideoUrl: string | null;
  playedRanges: PlayedRange[];
  lastPosition: number | null;
  durationSeconds: number | null;
  scrubbed: boolean;
};

export type IntroVideoGalleryHandlers = {
  onVideoPlay: (item: GalleryVideoItem) => void;
  onVideoTimeUpdate: (update: VideoTimeUpdate) => void;
  onVideoSeek: () => void;
};

// Players report the position about every 250ms, so a bigger forward step
// between updates is a seek rather than playback. The count is approximate: a
// slow scrub in smaller steps counts as watched, and a gap between late updates
// counts as unwatched.
const MAX_PLAYBACK_STEP_SECONDS = 1.5;

const newWatchSession = (): WatchSession => ({
  playedVideoUrl: null,
  playedRanges: [],
  lastPosition: null,
  durationSeconds: null,
  scrubbed: false,
});

const addPlayedRange = (ranges: PlayedRange[], [start, end]: PlayedRange): PlayedRange[] => {
  const merged: PlayedRange[] = [];
  let next: PlayedRange = [start, end];
  ranges.forEach((range) => {
    if (range[1] < next[0] || range[0] > next[1]) {
      merged.push(range);
    } else {
      next = [Math.min(range[0], next[0]), Math.max(range[1], next[1])];
    }
  });
  return [...merged, next];
};

const playedLength = (ranges: PlayedRange[]): number =>
  ranges.reduce((total, [start, end]) => total + end - start, 0);

const percentWatched = (secondsWatched: number, durationSeconds: number | null): number | null =>
  durationSeconds ? Math.min(100, Math.round((secondsWatched / durationSeconds) * 100)) : null;

/**
 * Returns the `ImageGallery` video handlers that report a sitter's intro video
 * being viewed, and how much of it was watched once the lightbox closes.
 */
const useIntroVideoTracking = (params: Params): IntroVideoGalleryHandlers => {
  const { entryPoint, sitterOpk, isLightboxOpen, cbsa = null } = params;
  const onIntroVideoView = useIntroVideoViewTracking(params);
  const viewerOpk = useCurrentUser({ refetchOnMount: false }).user?.opk ?? null;
  const sessionRef = useRef<WatchSession>(newWatchSession());

  useEffect(() => {
    if (isLightboxOpen) return;

    const { playedVideoUrl, playedRanges, durationSeconds, scrubbed } = sessionRef.current;
    sessionRef.current = newWatchSession();
    if (playedVideoUrl === null) return;

    const secondsWatched = Math.round(playedLength(playedRanges));

    emitAnalyticsEvent(
      new IntroVideoWatchProgressEvent({
        entry_point: entryPoint,
        video_url: playedVideoUrl,
        sitter_opk: sitterOpk,
        viewer_opk: viewerOpk,
        cbsa,
        platform: getPlatform(),
        seconds_watched: secondsWatched,
        duration_seconds: durationSeconds,
        percent_watched: percentWatched(secondsWatched, durationSeconds),
        scrubbed,
      })
    );
  }, [isLightboxOpen, entryPoint, sitterOpk, viewerOpk, cbsa]);

  const onVideoPlay = useCallback(
    (item: GalleryVideoItem): void => {
      onIntroVideoView(item);
      sessionRef.current.playedVideoUrl = item.video.sources[0] ?? '';
    },
    [onIntroVideoView]
  );

  const onVideoTimeUpdate = useCallback(({ currentTime, duration }: VideoTimeUpdate): void => {
    const session = sessionRef.current;
    const { lastPosition } = session;
    session.lastPosition = currentTime;

    // Both players also report the position a seek lands on, which never played.
    if (
      lastPosition !== null &&
      currentTime > lastPosition &&
      currentTime - lastPosition <= MAX_PLAYBACK_STEP_SECONDS
    ) {
      session.playedRanges = addPlayedRange(session.playedRanges, [lastPosition, currentTime]);
    }
    if (Number.isFinite(duration) && duration > 0) {
      session.durationSeconds = duration;
    }
  }, []);

  const onVideoSeek = useCallback((): void => {
    sessionRef.current.scrubbed = true;
  }, []);

  return { onVideoPlay, onVideoTimeUpdate, onVideoSeek };
};

export default useIntroVideoTracking;
